import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { Select } from '@/components/Select'
import {
  useChallengeBreakdown,
  useChallengeDashboard,
  useChallengeEntry,
  useChallengePlayers,
  useChallengeRanking,
  useFantasyChallenge,
  useSaveChallengeDraft,
  useShareFantasyChallenge,
  useSubmitChallengeEntry,
  type ChallengeFormation,
  type ChallengeIcon,
  type ChallengePlayer,
  type ChallengeRule,
  type ChallengeSelection,
} from '@/features/fantasy-challenges/api'
import { useTranslation } from '@/i18n'
import { pointsAccentColor } from '@/lib/pointsColor'
import { shareOrCopy } from '@/lib/share'

function slotKey(slot: string, index: number) {
  return `${slot}:${index}`
}

function getFormationSlots(formation?: ChallengeFormation | null) {
  if (!formation?.slots) return []
  return Object.entries(formation.slots).flatMap(([slot, count]) =>
    Array.from({ length: count }, (_, index) => ({ slot, index: index + 1 })),
  )
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
}

export function FantasyChallengePage() {
  const { challengeId } = useParams()
  const id = Number(challengeId)
  const { t } = useTranslation()
  const challengeQuery = useFantasyChallenge(id)
  const challenge = challengeQuery.data
  const [formationKey, setFormationKey] = useState('')
  const [selected, setSelected] = useState<Record<string, ChallengePlayer>>({})
  const [activeSlot, setActiveSlot] = useState<{ slot: string; index: number } | null>(null)
  const [search, setSearch] = useState('')
  const [teamId, setTeamId] = useState<number | undefined>()
  const [playerPage, setPlayerPage] = useState(1)
  const [notice, setNotice] = useState<string | null>(null)
  const [breakdownPlayerId, setBreakdownPlayerId] = useState<number | null>(null)
  const [shareState, setShareState] = useState<'idle' | 'success' | 'error'>('idle')
  const shareChallenge = useShareFantasyChallenge(id)
  const initializedFromEntry = useRef(false)
  const deferredSearch = useDeferredValue(search.trim())
  const isClosed = challenge?.status !== 'open'

  const playersQuery = useChallengePlayers(id, {
    slot: activeSlot?.slot,
    q: deferredSearch || undefined,
    teamId,
    page: playerPage,
    perPage: 50,
  }, activeSlot !== null && !isClosed)
  const entryQuery = useChallengeEntry(id)
  const scoreQuery = entryQuery
  const saveDraft = useSaveChallengeDraft(id)
  const submitEntry = useSubmitChallengeEntry(id)
  const dashboardQuery = useChallengeDashboard(id, Boolean(isClosed))
  const rankingQuery = useChallengeRanking(id, Boolean(isClosed))
  const breakdownQuery = useChallengeBreakdown(id, breakdownPlayerId)

  useEffect(() => {
    const lineup = entryQuery.data?.lineup
    if (!lineup || formationKey) return
    const nextFormation = lineup.selected_formation ?? lineup.default_formation ?? lineup.formations[0]?.key ?? ''
    setFormationKey(nextFormation)
  }, [entryQuery.data?.lineup, formationKey])

  useEffect(() => {
    if (!entryQuery.data || initializedFromEntry.current) return
    const players = entryQuery.data.entry?.players ?? []
    const next = players.reduce<Record<string, ChallengePlayer>>((result, player) => {
      if (player.slot && player.slot_index) result[slotKey(player.slot, player.slot_index)] = player
      return result
    }, {})
    setSelected(next)
    initializedFromEntry.current = true
  }, [entryQuery.data])

  const formations = entryQuery.data?.lineup?.formations ?? playersQuery.data?.lineup.formations ?? []
  const formation = formations.find((item) => item.key === formationKey) ?? formations[0]
  const slots = useMemo(() => getFormationSlots(formation), [formation])
  const selectedCount = Object.keys(selected).length
  const selectedPlayerIds = useMemo(() => new Set(Object.values(selected).map((player) => player.player_id)), [selected])
  const activePlayer = activeSlot ? selected[slotKey(activeSlot.slot, activeSlot.index)] : undefined
  const isSubmitted = entryQuery.data?.lineup?.is_submitted === true || entryQuery.data?.entry?.is_submitted === true
  const scorePlayers = scoreQuery.data?.score?.player_scores ?? []
  const pointsByPlayerId = useMemo(
    () => new Map(scorePlayers.map((player) => [player.player_id, player.points ?? 0])),
    [scorePlayers],
  )

  function selectPlayer(player: ChallengePlayer) {
    if (!activeSlot || player.is_selectable === false) return
    const currentKey = slotKey(activeSlot.slot, activeSlot.index)
    const alreadySelectedKey = Object.entries(selected).find(([, value]) => value.player_id === player.player_id)?.[0]
    setSelected((current) => {
      const next = { ...current }
      if (alreadySelectedKey && alreadySelectedKey !== currentKey) delete next[alreadySelectedKey]
      next[currentKey] = { ...player, slot: activeSlot.slot, slot_index: activeSlot.index }
      return next
    })
    setActiveSlot(null)
    setSearch('')
    setPlayerPage(1)
  }

  function payload(): { formation: string; players: ChallengeSelection[] } {
    return {
      formation: formationKey,
      players: Object.entries(selected).map(([key, player]) => {
        const [slot, index] = key.split(':')
        return { slot: slot ?? '', slot_index: Number(index ?? 0), player_id: player.player_id }
      }),
    }
  }

  async function handleInvite() {
    if (shareChallenge.isPending) return
    setShareState('idle')
    try {
      const share = await shareChallenge.mutateAsync()
      await shareOrCopy({ title: share.title, text: share.share_message, url: share.share_url })
      setShareState('success')
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return
      setShareState('error')
    }
  }

  function save() {
    setNotice(null)
    saveDraft.mutate(payload(), {
      onSuccess: () => setNotice(t('Je concept is opgeslagen.')),
      onError: (error) => setNotice(error.message),
    })
  }

  function submit() {
    setNotice(null)
    if (!formationKey || selectedCount < (formation?.lineup_size ?? 11)) {
      setNotice(t('Selecteer eerst een volledig elftal.'))
      return
    }
    submitEntry.mutate(payload(), {
      onSuccess: () => setNotice(t('Je challenge-team is ingediend.')),
      onError: (error) => setNotice(error.message),
    })
  }

  if (challengeQuery.isLoading || entryQuery.isLoading) {
    return <div className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-8 text-sm text-surface-400">{t('Challenge laden…')}</div>
  }

  if (challengeQuery.isError || !challenge) {
    return <section className="rounded-[1.5rem] border border-red-300/15 bg-red-300/[0.04] p-6 text-sm text-red-200">{t('Deze challenge kon niet worden geladen.')}</section>
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <Link to="/dashboard/manager/challenges" className="inline-flex items-center gap-2 text-sm font-bold text-accent-200 hover:text-white">← {t('Alle challenges')}</Link>

      <header className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
        <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{challenge.scope_type === 'international' ? t('Internationale challenge') : t('Competitie challenge')}</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-white">{challenge.title}</h1>
            {challenge.description ? <p className="mt-3 max-w-2xl text-sm leading-6 text-surface-400">{challenge.description}</p> : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <div className="flex gap-2">
              <div className="rounded-xl border border-white/[0.08] bg-[#111719] px-4 py-3 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-surface-500">{isClosed ? t('Status') : t('Deadline')}</p>
                <p className="mt-1 text-sm font-bold text-white">{isClosed ? (challenge.status === 'completed' ? t('Afgerond') : t('Scoring')) : formatDate(challenge.submission_deadline_at)}</p>
              </div>
              <div className="rounded-xl border border-white/[0.08] bg-[#111719] px-4 py-3 text-right">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-surface-500">{t('Deelnemers')}</p>
                <p className="mt-1 text-sm font-bold text-white">{challenge.participants_count ?? 0}</p>
              </div>
            </div>
            <button type="button" onClick={handleInvite} disabled={shareChallenge.isPending} className="inline-flex items-center gap-2 rounded-xl border border-white/[0.12] bg-[#151a1c] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-white/[0.09] disabled:cursor-wait disabled:opacity-60">
              {shareChallenge.isPending ? t('Link maken…') : t('Uitnodigen')}
            </button>
            {shareState === 'success' ? <p className="text-xs font-semibold text-emerald-300">{t('Link gedeeld of gekopieerd.')}</p> : null}
            {shareState === 'error' ? <p className="text-xs font-semibold text-red-300">{t('Delen is niet gelukt.')}</p> : null}
          </div>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,0.75fr)]">
        <div className="min-w-0 space-y-5">
          <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Jouw team')}</p>
                <h2 className="mt-1 text-xl font-black text-white">{t('Stel je challenge-team samen')}</h2>
                <p className="mt-1 text-sm text-surface-400">{selectedCount}/{formation?.lineup_size ?? 11} {t('spelers geselecteerd')}</p>
              </div>
              <label className="text-sm font-semibold text-surface-300">
                <span className="sr-only">{t('Formatie')}</span>
                <Select value={formationKey} onChange={(value) => { setFormationKey(value); setSelected({}); setActiveSlot(null) }} disabled={isClosed} options={formations.map((item) => ({ value: item.key, label: item.label || item.code || item.key }))} placeholder={t('Kies formatie')} ariaLabel={t('Formatie')} />
              </label>
            </div>
          </section>
          <ChallengeFormationPitch
            slots={slots}
            selectedPlayers={selected}
            activeSlot={activeSlot}
            canEdit={!isClosed}
            pointsByPlayerId={pointsByPlayerId}
            onSelect={(slot) => { setActiveSlot(slot); setPlayerPage(1) }}
            onPlayerTap={(player) => setBreakdownPlayerId(player.player_id)}
          />

          {!isClosed ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={save} disabled={saveDraft.isPending} className="rounded-xl border border-accent-300/25 px-4 py-3 text-sm font-bold text-accent-100 transition hover:bg-accent-300/[0.08] disabled:opacity-50">{saveDraft.isPending ? t('Opslaan…') : t('Concept opslaan')}</button>
              <button type="button" onClick={submit} disabled={submitEntry.isPending} className="rounded-xl bg-accent-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-accent-500 disabled:opacity-50">{submitEntry.isPending ? t('Opslaan…') : isSubmitted ? t('Wijzigingen opslaan') : t('Team indienen')}</button>
            </div>
          ) : null}
          {notice ? <p className="mt-4 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2.5 text-sm text-surface-300">{notice}</p> : null}
        </div>

        <aside className="space-y-6">
          <ScopePanel scopeType={challenge.scope_type} icons={challenge.icons ?? []} rules={challenge.rules} />
          {isClosed ? <ResultsPanel dashboard={dashboardQuery.data?.dashboard} ranking={rankingQuery.data?.ranking ?? []} /> : <RulesPanel challenge={challenge} />}
        </aside>
      </div>

      {activeSlot && !isClosed ? (
        <PlayerPicker
          activeSlot={activeSlot}
          activePlayer={activePlayer}
          query={playersQuery}
          selectedPlayerIds={selectedPlayerIds}
          selectedTeamId={teamId}
          search={search}
          onSearch={(value) => { setSearch(value); setPlayerPage(1) }}
          onTeamChange={(value) => { setTeamId(value); setPlayerPage(1) }}
          onPageChange={setPlayerPage}
          onSelect={selectPlayer}
          onClose={() => setActiveSlot(null)}
        />
      ) : null}
      {breakdownPlayerId !== null ? <BreakdownPanel player={breakdownQuery.data?.player} matches={breakdownQuery.data?.matches ?? []} isLoading={breakdownQuery.isLoading} onClose={() => setBreakdownPlayerId(null)} /> : null}
    </div>
  )
}

function ChallengeFormationPitch({
  slots,
  selectedPlayers,
  activeSlot,
  canEdit,
  pointsByPlayerId,
  onSelect,
  onPlayerTap,
}: {
  slots: Array<{ slot: string; index: number }>
  selectedPlayers: Record<string, ChallengePlayer>
  activeSlot: { slot: string; index: number } | null
  canEdit: boolean
  pointsByPlayerId?: Map<number, number>
  onSelect: (slot: { slot: string; index: number }) => void
  onPlayerTap: (player: ChallengePlayer) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="manager-formation-pitch relative mt-5 min-h-[28rem] overflow-hidden rounded-[1.25rem] border border-emerald-200/10 bg-[#0b3028]">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-x-5 top-5 bottom-5 rounded-xl border border-white/15" />
        <div className="absolute inset-x-5 top-1/2 h-px bg-white/15" />
        <div className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15" />
        <div className="absolute left-1/2 top-5 h-20 w-36 -translate-x-1/2 rounded-b-xl border-x border-b border-white/15" />
        <div className="absolute bottom-5 left-1/2 h-20 w-36 -translate-x-1/2 rounded-t-xl border-x border-t border-white/15" />
      </div>

      <div className="relative flex min-h-[28rem] flex-col justify-between gap-4 px-4 py-6 sm:px-8 sm:py-8">
        {groupChallengeSlots(slots).map(([group, groupItems]) => (
          <div
            key={group}
            className="flex w-full flex-nowrap justify-center gap-0.5 sm:gap-5"
          >
            {groupItems.map((slot) => {
              const key = slotKey(slot.slot, slot.index)
              const player = selectedPlayers[key]
              const isActive = activeSlot ? slotKey(activeSlot.slot, activeSlot.index) === key : false
              const points = player ? pointsByPlayerId?.get(player.player_id) : undefined

              return (
                <button
                  key={key}
                  type="button"
                  disabled={!canEdit && !player}
                  onClick={() => player && !canEdit ? onPlayerTap(player) : onSelect(slot)}
                  className={[
                    'manager-player-slot relative z-10 flex min-w-0 max-w-[4.75rem] flex-1 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-center transition sm:w-24',
                    isActive ? 'bg-accent-300/15 ring-2 ring-accent-300/50' : 'hover:bg-white/[0.07]',
                    !canEdit && !player ? 'cursor-default opacity-60' : '',
                  ].join(' ')}
                >
                  <span className="relative">
                    <ChallengePlayerAvatar src={player?.image_path} name={player?.name || group} />
                    {player?.is_new_in_round ? (
                      <span
                        className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-[#0b3028] bg-[#111820] text-accent-200"
                        title={t('Nieuwe speler deze speelronde')}
                        aria-label={t('Nieuwe speler deze speelronde')}
                      >
                        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M2.5 5.5h10M10 2.5l3 3-3 3M13.5 10.5h-10M6 7.5l-3 3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                    ) : null}
                    {points !== undefined ? (
                      <span
                        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-full px-1.5 py-px text-[10px] font-black text-black"
                        style={{ background: pointsAccentColor(points) }}
                      >
                        {points}
                      </span>
                    ) : null}
                  </span>
                  <span className="w-full truncate text-[10px] font-bold text-white sm:text-xs">
                    {player?.name || `${group} ${slot.index}`}
                  </span>
                  <span className="max-w-full truncate text-[9px] font-semibold uppercase tracking-[0.1em] text-white/50">
                    {player ? (player.team_display_name || player.team_name || group) : 'Kies'}
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

function groupChallengeSlots(slots: Array<{ slot: string; index: number }>) {
  const grouped = slots.reduce<Record<string, Array<{ slot: string; index: number }>>>((groups, slot) => {
    groups[slot.slot] = [...(groups[slot.slot] ?? []), slot]
    return groups
  }, {})
  const order = ['GK', 'DEF', 'MID', 'FWD', 'ATT']

  return Object.entries(grouped).sort(([left], [right]) => {
    const leftIndex = order.indexOf(left.toUpperCase())
    const rightIndex = order.indexOf(right.toUpperCase())
    return (leftIndex === -1 ? order.length : leftIndex) - (rightIndex === -1 ? order.length : rightIndex) || left.localeCompare(right)
  })
}

function ChallengePlayerAvatar({ src, name }: { src: string | null | undefined; name?: string | null }) {
  return src ? (
    <img src={src} alt="" className="h-11 w-11 rounded-full border-2 border-white/20 bg-[#172024] object-cover shadow-lg sm:h-14 sm:w-14" />
  ) : (
    <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white/20 bg-[#172024] text-sm font-bold text-accent-200 shadow-lg sm:h-14 sm:w-14">
      {(name || 'S').slice(0, 1).toUpperCase()}
    </span>
  )
}

function ScopePanel({ scopeType, icons, rules }: { scopeType: string; icons: ChallengeIcon[]; rules: ChallengeRule[] }) {
  const { t } = useTranslation()
  const clubsRule = rules.find((rule) => rule.type === 'team_whitelist')
  const clubs = clubsRule?.config?.clubs ?? []
  const seasonsRule = rules.find((rule) => rule.type === 'season_whitelist')
  const seasons = seasonsRule?.config?.seasons ?? []
  const iconByCountryId = new Map(icons.filter((icon) => icon.country_id != null).map((icon) => [icon.country_id, icon]))

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Spelers uit de volgende competitie(s)')}</p>
      <h2 className="mt-1 text-lg font-black text-white">{scopeType === 'international' ? t('Uit welke competities?') : t('Uit welke competitie?')}</h2>

      <div className="mt-4 space-y-2">
        {scopeType === 'international'
          ? (seasons.length
            ? seasons.map((season) => {
              const icon = season.country_id != null ? iconByCountryId.get(season.country_id) : undefined
              return (
                <div key={season.season_id} className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-[#111719] px-3 py-2.5">
                  {icon?.image_path ? <img src={icon.image_path} alt="" className="h-6 w-6 shrink-0 rounded-sm object-contain" /> : null}
                  <p className="min-w-0 truncate text-sm font-semibold text-surface-200">{season.league_name ?? season.name ?? t('Competitie')}</p>
                </div>
              )
            })
            // Oudere challenges hebben geen season_whitelist-regel opgeslagen; val dan terug
            // op de landen-iconen (altijd aanwezig voor scope `international`) zodat er nooit
            // "onbekend" staat terwijl de data er wel degelijk is.
            : (icons.length
              ? icons.map((icon) => (
                <div key={icon.country_id ?? icon.image_path} className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-[#111719] px-3 py-2.5">
                  {icon.image_path ? <img src={icon.image_path} alt="" className="h-6 w-6 shrink-0 rounded-sm object-contain" /> : null}
                  <p className="min-w-0 truncate text-sm font-semibold text-surface-200">{icon.country_name ?? t('Competitie')}</p>
                </div>
              ))
              : <p className="text-sm text-surface-400">{t('Onbekende competities.')}</p>))
          : (icons[0]
            ? <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-[#111719] px-3 py-2.5">
              {icons[0].image_path ? <img src={icons[0].image_path} alt="" className="h-8 w-8 shrink-0 rounded-sm object-contain" /> : null}
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-white">{icons[0].league_name ?? t('Competitie')}</p>
                {icons[0].country_name ? <p className="truncate text-xs text-surface-400">{icons[0].country_name}</p> : null}
              </div>
            </div>
            : <p className="text-sm text-surface-400">{t('Onbekende competitie.')}</p>)}
      </div>

      <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Toegestane clubs')}</p>
      {clubs.length ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {clubs.map((club) => (
            <div key={club.team_id} className="flex items-center gap-2 rounded-lg border border-white/[0.07] bg-[#111719] px-2.5 py-2">
              {club.image_path ? <img src={club.image_path} alt="" className="h-4 w-4 shrink-0 rounded-sm object-contain" /> : null}
              <span className="min-w-0 truncate text-xs font-semibold text-surface-300">{club.name}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-sm text-surface-400">{t('Alle clubs uit {scope} zijn toegestaan.', { scope: scopeType === 'international' ? t('deze competities') : t('deze competitie') })}</p>
      )}
    </section>
  )
}

function RulesPanel({ challenge }: { challenge: { rules: Array<{ description?: string | null }> } }) {
  const { t } = useTranslation()
  return <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5"><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Spelregels')}</p><h2 className="mt-1 text-lg font-black text-white">{t('Waar moet je rekening mee houden?')}</h2><div className="mt-4 space-y-2">{challenge.rules.length ? challenge.rules.map((rule, index) => <div key={index} className="flex items-start gap-3 rounded-xl border border-white/[0.07] bg-[#111719] px-3 py-3"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-300/10 text-xs font-black text-accent-200" aria-hidden="true">✓</span><p className="min-w-0 text-sm leading-5 text-surface-300">{rule.description}</p></div>) : <p className="text-sm text-surface-400">{t('Kies je beste elftal binnen de deadline.')}</p>}</div></section>
}

function ResultsPanel({ dashboard, ranking }: { dashboard?: { total_entries?: number; my_rank?: number | null; total_points?: number } | undefined; ranking: Array<{ rank?: number; display_name?: string | null; total_points?: number }> }) {
  const { t } = useTranslation()
  return <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5"><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Resultaat')}</p><div className="mt-4 grid grid-cols-3 gap-2"><Stat label={t('Punten')} value={dashboard?.total_points ?? 0} /><Stat label={t('Rang')} value={dashboard?.my_rank ?? '—'} /><Stat label={t('Teams')} value={dashboard?.total_entries ?? ranking.length} /></div><div className="mt-5 space-y-2">{ranking.slice(0, 5).map((row, index) => <div key={`${row.rank ?? index}-${row.display_name}`} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2 text-sm"><span className="font-semibold text-white">{row.rank ?? index + 1}. {row.display_name ?? t('Speler')}</span><span className="text-surface-400">{row.total_points ?? 0} pnt</span></div>)}</div></section>
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return <div className="rounded-xl bg-white/[0.04] p-3 text-center"><p className="text-lg font-black text-white">{value}</p><p className="mt-1 text-[10px] uppercase tracking-[0.12em] text-surface-500">{label}</p></div>
}

function PlayerPicker({ activeSlot, activePlayer, query, selectedPlayerIds, selectedTeamId, search, onSearch, onTeamChange, onPageChange, onSelect, onClose }: { activeSlot: { slot: string; index: number }; activePlayer?: ChallengePlayer | undefined; query: ReturnType<typeof useChallengePlayers>; selectedPlayerIds: Set<number>; selectedTeamId?: number | undefined; search: string; onSearch: (value: string) => void; onTeamChange: (teamId: number | undefined) => void; onPageChange: (page: number) => void; onSelect: (player: ChallengePlayer) => void; onClose: () => void }) {
  const { t } = useTranslation()
  const [stableTeams, setStableTeams] = useState(query.data?.teams ?? [])
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (query.data?.teams) setStableTeams(query.data.teams)
  }, [query.data?.teams])

  function handleTeamChange(teamId: number | undefined) {
    onTeamChange(teamId)
    window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' }))
  }

  return (
    <div className="fixed inset-0 z-50">
      <button type="button" onClick={onClose} aria-label={t('Sluit spelerselectie')} className="animate-drawer-backdrop-in absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <section role="dialog" aria-modal="true" aria-labelledby="challenge-player-picker-title" className="animate-manager-player-picker-in absolute inset-x-0 bottom-0 mx-auto flex h-[92dvh] max-h-[92dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.5rem] border-t border-white/[0.1] bg-[#0d1113] shadow-[0_-20px_64px_rgba(0,0,0,0.5)]">
        <header className="shrink-0 border-b border-white/[0.07] p-5 sm:p-6">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Spelers bekijken')}</p>
              <h2 id="challenge-player-picker-title" className="mt-1 text-xl font-black tracking-tight text-white sm:text-2xl">{activePlayer ? t('Vervang speler') : t('Selecteer speler')}</h2>
              <p className="mt-1 text-sm text-surface-500">{t('Positie {position}', { position: `${activeSlot.slot} ${activeSlot.index}` })}</p>
            </div>
            <ModalCloseButton onClick={onClose} label={t('Sluiten')} className="h-9 w-9" />
          </div>
          {activePlayer ? <div className="mt-5 flex items-center gap-4 rounded-2xl border border-accent-300/15 bg-accent-300/[0.06] p-4 sm:p-5"><ChallengePlayerAvatar src={activePlayer.image_path} name={activePlayer.name} /><div className="min-w-0 flex-1"><p className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent-200">{t('Huidige speler')}</p><p className="mt-1 truncate text-base font-bold text-white">{activePlayer.name}</p><p className="mt-1 truncate text-sm text-surface-400">{activePlayer.team_display_name || activePlayer.team_name || t('Club onbekend')}{activePlayer.position_group ? ` · ${activePlayer.position_group}` : ''}{formatPlayerFacts(activePlayer)}</p></div></div> : null}
        </header>

        <div ref={contentRef} className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-5 sm:p-6">
          <div className="grid min-w-0 gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
            <aside className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Clubs')}</p>
              <div className="no-scrollbar mt-3 flex w-full min-w-0 max-w-full touch-pan-x gap-2 overflow-x-auto overscroll-x-contain pb-1 lg:grid lg:grid-cols-1 lg:overflow-visible lg:pb-0">
                <button type="button" onClick={() => handleTeamChange(undefined)} className={['shrink-0 rounded-xl border px-3 py-2.5 text-left text-xs font-bold transition lg:w-full', selectedTeamId === undefined ? 'border-accent-300/25 bg-accent-300/10 text-accent-100' : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:text-white'].join(' ')}>{t('Alle clubs')}</button>
                {stableTeams.map((team) => <button key={team.id} type="button" onClick={() => handleTeamChange(team.id)} className={['shrink-0 rounded-xl border px-3 py-2.5 text-left text-xs font-bold transition lg:w-full', selectedTeamId === team.id ? 'border-accent-300/25 bg-accent-300/10 text-accent-100' : 'border-white/[0.08] bg-[#111719] text-surface-400 hover:text-white'].join(' ')}>{team.display_name || team.name || `Club ${team.id}`}</button>)}
              </div>
            </aside>

            <div className="min-w-0">
              <div className="relative mt-0"><span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-surface-600">⌕</span><input autoFocus value={search} onChange={(event) => onSearch(event.target.value)} placeholder={t('Zoek speler')} className="w-full rounded-xl border border-white/[0.08] bg-[#111719] py-3 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-surface-600 focus:border-accent-300/30" /></div>
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {query.isLoading ? <p className="py-8 text-center text-sm text-surface-500">{t('Spelers laden…')}</p> : null}
                {query.isError ? <p className="py-8 text-center text-sm text-red-300">{query.error instanceof Error ? query.error.message : t('Spelers konden niet worden geladen.')}</p> : null}
                {!query.isLoading && !query.isError && query.data?.players.length === 0 ? <p className="py-8 text-center text-sm text-surface-500">{t('Geen spelers gevonden.')}</p> : null}
                {query.data?.players.map((player) => <ChallengePlayerOption key={player.player_id} player={player} isCurrent={player.player_id === activePlayer?.player_id} isSelectedElsewhere={player.player_id !== activePlayer?.player_id && selectedPlayerIds.has(player.player_id)} canEdit={true} onSelect={onSelect} />)}
                {query.data?.pagination && query.data.pagination.last_page > 1 ? <div className="mt-3 flex items-center justify-between gap-3"><button type="button" disabled={query.isFetching || query.data.pagination.page <= 1} onClick={() => onPageChange(query.data!.pagination!.page - 1)} className="rounded-lg border border-white/[0.1] px-3 py-2 text-xs font-bold text-surface-300 disabled:opacity-40">{t('Vorige')}</button><span className="text-xs text-surface-500">{t('Pagina {page} van {total}', { page: query.data.pagination.page, total: query.data.pagination.last_page })}</span><button type="button" disabled={query.isFetching || !query.data.pagination.has_more} onClick={() => onPageChange(query.data!.pagination!.page + 1)} className="rounded-lg border border-white/[0.1] px-3 py-2 text-xs font-bold text-surface-300 disabled:opacity-40">{t('Volgende')}</button></div> : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

function ChallengePlayerOption({ player, isCurrent, isSelectedElsewhere, canEdit, onSelect }: { player: ChallengePlayer; isCurrent: boolean; isSelectedElsewhere: boolean; canEdit: boolean; onSelect: (player: ChallengePlayer) => void }) {
  const { t } = useTranslation()
  const unavailable = player.is_selectable === false
  const blocked = !isCurrent && (unavailable || isSelectedElsewhere)
  const status = isCurrent ? t('Huidige speler') : isSelectedElsewhere ? t('Al gekozen') : unavailable ? player.selection_status_message || t('Niet beschikbaar') : t('Vervang door deze speler')

  return <button type="button" disabled={!canEdit || blocked} onClick={() => onSelect(player)} className={['flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition', isCurrent ? 'border-accent-300/20 bg-accent-300/[0.07]' : blocked ? 'border-white/[0.06] bg-[#101516] opacity-60' : 'border-white/[0.07] bg-[#111719] hover:border-accent-300/25 hover:bg-[#151c1e]', !canEdit || blocked ? 'cursor-not-allowed' : ''].join(' ')}><ChallengePlayerAvatar src={player.image_path} name={player.name} /><span className="min-w-0 flex-1"><span className="flex items-center gap-2"><span className="truncate text-sm font-semibold text-white">{player.name || t('Speler')}</span>{player.jersey_number ? <span className="shrink-0 text-[10px] font-bold text-surface-600">#{player.jersey_number}</span> : null}</span><span className="mt-1 block truncate text-xs text-surface-500">{player.team_display_name || player.team_name || t('Club onbekend')}{player.position_group ? ` · ${player.position_group}` : ''}{formatPlayerFacts(player)}</span></span><span className={['shrink-0 text-right text-[10px] font-bold', isCurrent ? 'text-accent-200' : blocked ? 'text-amber-300' : 'text-accent-200'].join(' ')}>{status}</span></button>
}

function formatPlayerFacts(player: ChallengePlayer) {
  const facts: string[] = []
  if (player.age !== null && player.age !== undefined && Number.isFinite(Number(player.age))) facts.push(`${Math.floor(Number(player.age))} jaar`)
  if (player.height_cm !== null && player.height_cm !== undefined) facts.push(`${player.height_cm} cm`)
  if (player.preferred_foot === 'left') facts.push('links')
  if (player.preferred_foot === 'right') facts.push('rechts')
  if (player.preferred_foot === 'both') facts.push('beide voeten')
  return facts.length ? ` · ${facts.join(' · ')}` : ''
}

function BreakdownPanel({ player, matches, isLoading, onClose }: { player: ChallengePlayer | undefined; matches: Array<Record<string, unknown>>; isLoading: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  return <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-6"><section role="dialog" aria-modal="true" className="w-full max-w-xl rounded-t-[1.5rem] border border-white/[0.1] bg-[#0d1113] p-5 sm:rounded-[1.5rem]"><div className="flex items-start justify-between"><div><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent-200">{t('Punten breakdown')}</p><h2 className="mt-1 text-xl font-black text-white">{player?.name ?? t('Speler')}</h2></div><button type="button" onClick={onClose} className="text-2xl text-surface-400 hover:text-white" aria-label={t('Sluiten')}>×</button></div>{isLoading ? <p className="mt-6 text-sm text-surface-400">{t('Punten laden…')}</p> : <div className="mt-5 space-y-2">{matches.length === 0 ? <p className="text-sm text-surface-400">{t('Nog geen wedstrijdpunten beschikbaar.')}</p> : matches.map((match, index) => { const nestedBreakdown = match.breakdown as { total?: number } | undefined; const nestedMatch = match.match as { home_team?: { name?: string }; away_team?: { name?: string } } | undefined; const label = String(match.fixture_name ?? match.name ?? [nestedMatch?.home_team?.name, nestedMatch?.away_team?.name].filter(Boolean).join(' — ') ?? `Wedstrijd ${index + 1}`); const points = match.points ?? match.total_points ?? nestedBreakdown?.total ?? 0; return <div key={index} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2 text-sm text-surface-300"><span>{label}</span><strong className="text-white">{String(points)} pnt</strong></div> })}</div>}</section></div>
}
